/* ------------------------------------------------------------------------------
 *
 *  # Familias Components
 *
 *  Gestión de familias con DataTables
 *
 * ---------------------------------------------------------------------------- */

import { 
    initGenericDataTable, 
    refreshDataTable, 
    destroyDataTable 
} from '../integrations/datatables.js';

// Variables de estado
let familiaGridCallbacks = null;

// Helper functions
function getStatusBadgeClass(isActive) {
    return isActive ? 'bg-success' : 'bg-secondary';
}

function getStatusText(isActive) {
    return isActive ? 'Activo' : 'Inactivo';
}

// Inicializar DataTable
export function initFamiliasGrid(familias) {
    destroyDataTable('familiasTable');
    
    // Prepare data for DataTable
    const tableData = familias.map(familia => [
        // Actions column (first)
        generateFamiliaActions(familia.id),
        familia.apellido,
        generateMiembrosColumn(familia),
        generateEstudiantesColumn(familia),
        generateStatusColumn(familia),
        familia.createdAt ? new Date(familia.createdAt).toLocaleDateString('es-ES') : '-'
    ]);

    const options = {
        data: tableData,
        columns: [
            { title: 'Acciones', orderable: false, className: 'text-center no-export', width: '120px' },
            { title: 'Apellido', width: '20%' },
            { title: 'Miembros', width: '25%' },
            { title: 'Estudiantes', width: '25%' },
            { title: 'Estado', className: 'text-center', width: '10%' },
            { title: 'Fecha Registro', width: '15%' }
        ],
        exportTitle: 'Familias - Montessori Nova School',
        order: [[1, 'asc']], // Order by apellido
        buttons: [
            {
                extend: 'collection',
                className: 'btn btn-flat-primary btn-labeled btn-labeled-start dropdown-toggle me-3 rounded',
                text: '<span class="btn-labeled-icon bg-primary text-white rounded-start"><i class="ph-export"></i></span>Exportar',
                buttons: [
                    {
                        extend: 'excel',
                        text: '<i class="ph-file-xls me-1"></i>Excel',
                        className: 'dropdown-item',
                        title: 'Familias - Montessori Nova',
                        exportOptions: {
                            columns: [1, 2, 3, 4, 5],
                            format: {
                                body: function (inner, coldex, rowdex) {
                                    if (!inner || (typeof inner === 'string' && !inner.includes('<'))) {
                                        return inner;
                                    }
                                    const $el = $(inner);
                                    const exportText = $el.attr('data-export');
                                    if (exportText) {
                                        return exportText;
                                    }
                                    if (inner.length <= 0) return inner;
                                    let el = $.parseHTML(inner);
                                    let result = '';
                                    $.each(el, function (index, item) {
                                        if (item.innerText === undefined) {
                                            result = result + item.textContent;
                                        } else {
                                            result = result + item.innerText;
                                        }
                                    });
                                    return result;
                                }
                            }
                        }
                    },
                    {
                        extend: 'copy',
                        text: '<i class="ph-clipboard-text me-1"></i>Copiar',
                        className: 'dropdown-item',
                        exportOptions: {
                            columns: [1, 2, 3, 4, 5],
                            format: {
                                body: function (inner, coldex, rowdex) {
                                    if (!inner || (typeof inner === 'string' && !inner.includes('<'))) {
                                        return inner;
                                    }
                                    const $el = $(inner);
                                    const exportText = $el.attr('data-export');
                                    if (exportText) {
                                        return exportText;
                                    }
                                    if (inner.length <= 0) return inner;
                                    let el = $.parseHTML(inner);
                                    let result = '';
                                    $.each(el, function (index, item) {
                                        if (item.innerText === undefined) {
                                            result = result + item.textContent;
                                        } else {
                                            result = result + item.innerText;
                                        }
                                    });
                                    return result;
                                }
                            }
                        }
                    }
                ]
            }
        ]
    };

    return initGenericDataTable('familiasTable', options);
}

function generateFamiliaActions(familiaId) {
    return `
        <div class="d-inline-flex">
            <a href="javascript:void(0)" class="text-body me-2" onclick="window.sfBlazorSB.familias.viewFamilia(${familiaId})" title="Ver detalles">
                <i class="ph-eye"></i>
            </a>
            <a href="javascript:void(0)" class="text-body" onclick="window.sfBlazorSB.familias.deleteFamilia(${familiaId})" title="Eliminar">
                <i class="ph-trash"></i>
            </a>
        </div>
    `;
}

function generateMiembrosColumn(familia) {
    if (!familia.usuarios || familia.usuarios.length === 0) {
        return '<span class="text-muted" data-export="-">Sin miembros</span>';
    }
    
    const nombres = familia.usuarios.map(u => `${u.firstName} ${u.lastName}`).join(', ');
    let badges = '';
    
    familia.usuarios.slice(0, 3).forEach(usuario => {
        badges += `<span class="badge bg-primary me-1">${usuario.firstName} ${usuario.lastName}</span>`;
    });
    
    if (familia.usuarios.length > 3) {
        badges += `<span class="badge bg-secondary">+${familia.usuarios.length - 3} más</span>`;
    }
    
    return `<span data-export="${nombres}">${badges}</span>`;
}

function generateEstudiantesColumn(familia) {
    if (!familia.estudiantes || familia.estudiantes.length === 0) {
        return '<span class="text-muted" data-export="-">Sin estudiantes</span>';
    }
    
    const nombres = familia.estudiantes.map(e => `${e.firstName} ${e.lastName}`).join(', ');
    let badges = '';
    
    familia.estudiantes.slice(0, 3).forEach(estudiante => {
        badges += `<span class="badge bg-success me-1">${estudiante.firstName}</span>`;
    });
    
    if (familia.estudiantes.length > 3) {
        badges += `<span class="badge bg-secondary">+${familia.estudiantes.length - 3} más</span>`;
    }
    
    return `<span data-export="${nombres}">${badges}</span>`;
}

function generateStatusColumn(familia) {
    const statusBadgeClass = getStatusBadgeClass(familia.isActive);
    const statusText = getStatusText(familia.isActive);
    return `<span class="badge ${statusBadgeClass}" data-export="${statusText}">${statusText}</span>`;
}

// Refrescar DataTable
export function refreshFamiliasGrid(familias) {
    destroyDataTable('familiasTable');
    initFamiliasGrid(familias);
}

// Callbacks
export function setFamiliasCallbacks(dotNetRef) {
    familiaGridCallbacks = dotNetRef;
}

// Action functions
export function viewFamilia(familiaId) {
    if (familiaGridCallbacks) {
        familiaGridCallbacks.invokeMethodAsync('ViewFamilia', familiaId);
    }
}

export function editFamilia(familiaId) {
    if (familiaGridCallbacks) {
        familiaGridCallbacks.invokeMethodAsync('EditFamilia', familiaId);
    }
}

export function deleteFamilia(familiaId) {
    if (familiaGridCallbacks) {
        familiaGridCallbacks.invokeMethodAsync('DeleteFamilia', familiaId);
    }
}

// Filtros
export function applyFilters() {
    const globalSearch = document.getElementById('globalSearchFamilias')?.value || '';
    const statusFilter = document.getElementById('statusFilterFamilias')?.value || '';
    
    const table = $('#familiasTable').DataTable();

    // Clear previous search functions
    $.fn.dataTable.ext.search.splice(0);

    // Add custom search functions
    if (statusFilter) {
        $.fn.dataTable.ext.search.push(function(settings, data, dataIndex) {
            const isActive = statusFilter === 'true';
            return data[4].includes(isActive ? 'Activo' : 'Inactivo');
        });
    }

    // Apply global search
    table.search(globalSearch).draw();
}

export function clearFilters() {
    const globalSearch = document.getElementById('globalSearchFamilias');
    const statusFilter = document.getElementById('statusFilterFamilias');
    
    if (globalSearch) globalSearch.value = '';
    if (statusFilter) statusFilter.value = '';
    
    const table = $('#familiasTable').DataTable();
    $.fn.dataTable.ext.search.splice(0);
    table.search('').draw();
}
